import {
  Accordion,
  ActionIcon,
  CloseButton,
  Flex,
  Text,
  Transition,
  useMantineTheme,
} from "@mantine/core";
import {
  GetSignedAgreementsQuery,
  SignedAgreementStatus,
} from "src/generated/graphql";
import { currencyFormatter } from "src/utils/formatting";
import {
  AiFillEye,
  AiOutlinePauseCircle,
  AiOutlinePlayCircle,
} from "react-icons/ai";
import { useModals } from "@mantine/modals";
import ViewPolicies from "src/components/SignNowDocs/ViewPolicies";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useState } from "react";
import { BiBlock } from "react-icons/bi";
import { useClickOutside } from "@mantine/hooks";
import { Request } from ".";

interface AgreementListingProps {
  agreementData: GetSignedAgreementsQuery["signedAgreementMany"];
  setIsRequest: (e: boolean) => void;
  setSelectedAgreement: (e: Request) => void;
}

const AgreementListing = ({
  agreementData,
  setIsRequest,
  setSelectedAgreement,
}: AgreementListingProps) => {
  const theme = useMantineTheme();
  const modals = useModals();
  const [activeOption, setActiveOption] = useState<
    { name: string; id: string } | undefined
  >(undefined);
  const outsideClickRef = useClickOutside(() => {
    setActiveOption(undefined);
  });

  const handleOpenListMenu = (option: { name: string; id: string }) => {
    setActiveOption(option);
  };

  const viewPolicy = (signedAgreementId: string) => {
    modals.openModal({
      children: <ViewPolicies signedAgreementId={signedAgreementId} />,
      withCloseButton: true,
      closeOnClickOutside: true,
      title: "View Policies",
    });
  };
  return (
    <Flex direction={"column"}>
      <Text size={18} fw={600} color={theme.colors.secondaryTextColor[6]}>
        My Agreements
      </Text>

      {agreementData &&
        agreementData?.map((item) => {
          return (
            <Flex
              style={{
                background: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
                marginTop: "15px",
                padding: "15px",
                borderRadius: "10px",
              }}
              direction={"column"}
              py={5}
              key={item.id}
            >
              <Flex justify={"space-between"} align={"center"}>
                <Text
                  size={18}
                  fw={500}
                  color={theme.colors.primaryTextColor[6]}
                >
                  {item.agreement.name}
                </Text>
                <Flex>
                  <Text
                    size={14}
                    fw={400}
                    color={theme.colors.primaryTextColor[6]}
                  >
                    {item?.subscription?.subscriptionStatus}
                  </Text>
                  <ActionIcon
                    ml={4}
                    size="sm"
                    variant="transparent"
                    onClick={() => {}}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.colors.primary[2],
                      },
                    }}
                  >
                    <BsThreeDotsVertical
                      color={theme.colors.primaryTextColor[6]}
                      onClick={() => {
                        handleOpenListMenu({
                          name: item.agreement.name,
                          id: item.id,
                        });
                      }}
                    />
                  </ActionIcon>
                </Flex>

                <Transition
                  mounted={activeOption?.id === item.id}
                  transition="slide-left"
                  duration={200}
                  timingFunction="ease-in-out"
                >
                  {(styles) => (
                    <Flex
                      my={15}
                      justify="space-between"
                      px={10}
                      py={5}
                      bg={theme.colors.primary[4]}
                      style={{
                        ...styles,
                        position: "absolute",
                        width: "-webkit-fill-available",
                        marginRight: "25px",
                        borderRadius: "5px",
                      }}
                      ref={outsideClickRef}
                    >
                      <ActionIcon
                        variant="light"
                        color="blue.4"
                        size="md"
                        my={4}
                        w="45%"
                        mah={25}
                        onClick={() => {
                          setSelectedAgreement({
                            id: item.id,
                            name: item.agreement.name,
                            locationId: item.location.id,
                            status: SignedAgreementStatus.Frozen,
                            paymentFrequency: item.agreement.paymentFrequency,
                          });
                          setIsRequest(true);
                        }}
                        sx={{
                          backgroundColor: theme.colors.primary[4],
                          "&:hover": {
                            backgroundColor: theme.colors.primary[6],
                          },
                        }}
                      >
                        {item.status === SignedAgreementStatus.Frozen ? (
                          <>
                            <AiOutlinePlayCircle size={14} />
                            <Text mx={5}>Un-Freeze Agreement</Text>
                          </>
                        ) : (
                          <>
                            <AiOutlinePauseCircle size={14} />
                            <Text mx={5} size={14}>
                              Request a Freeze
                            </Text>
                          </>
                        )}
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        size="md"
                        color="red.4"
                        my={4}
                        w="45%"
                        mah={25}
                        onClick={() => {
                          setSelectedAgreement({
                            id: item.id,
                            name: item.agreement.name,
                            locationId: item.location.id,
                            status: SignedAgreementStatus.Cancelled,
                            paymentFrequency: item.agreement.paymentFrequency,
                          });
                          setIsRequest(true);
                        }}
                        sx={{
                          backgroundColor: theme.colors.primary[4],
                          "&:hover": {
                            backgroundColor: theme.colors.primary[6],
                          },
                        }}
                      >
                        <>
                          <BiBlock size={14} />
                          <Text mx={5} size={14}>
                            Request a Cancel
                          </Text>
                        </>
                      </ActionIcon>
                      <CloseButton
                        size="md"
                        c={theme.colors.light[5]}
                        onClick={() => setActiveOption(undefined)}
                        my={4}
                        sx={{
                          "&:hover": {
                            backgroundColor: theme.colors.primary[6],
                          },
                        }}
                      />
                    </Flex>
                  )}
                </Transition>
              </Flex>
              <Text
                size={14}
                fw={400}
                my={20}
                color={theme.colors.primaryTextColor[6]}
              >
                {item.agreement.description}
              </Text>

              <Accordion
                styles={{
                  control: {
                    padding: "0px",
                    "&:active": {
                      backgroundColor: "rgba(0,0,0,0)",
                    },
                  },
                  chevron: {
                    margin: "0px",
                  },
                  item: {
                    border: "0px",
                  },
                  content: {
                    padding: "0px",
                  },
                }}
                style={{ borderRadius: 4 }}
              >
                <Accordion.Item value={item.id}>
                  <Accordion.Control
                    style={{ color: theme.colors.primaryTextColor[6] }}
                  >
                    <Flex justify={"space-between"} align={"center"}>
                      {
                        <Text
                          size={14}
                          fw={500}
                          color={theme.colors.primaryTextColor[6]}
                        >
                          {`${currencyFormatter.format(
                            item?.subscription?.recurringCharges
                              .map((charge) => charge.cost)
                              .reduce((a, b) => a + b, 0)
                          )} ${item?.agreement?.paymentFrequency}`}
                        </Text>
                      }
                      <Text
                        size={14}
                        fw={400}
                        color={theme.colors.primaryTextColor[6]}
                      >
                        Next Payment{" "}
                        {new Date(
                          item?.subscription?.nextDraftDate
                        ).toLocaleDateString()}
                      </Text>
                    </Flex>
                  </Accordion.Control>
                  {item?.paymentMethod ? (
                    <Accordion.Panel>
                      <Flex direction={"column"} pr={25} py={10}>
                        <Flex
                          w="100%"
                          justify={"space-between"}
                          align={"center"}
                        >
                          <Text
                            size={12}
                            fw={400}
                            color={theme.colors.primaryTextColor[6]}
                          >
                            {`${item?.agreement?.Unit} ${item?.agreement?.termValue}`}
                          </Text>
                          <Text
                            size={12}
                            fw={400}
                            color={theme.colors.primaryTextColor[6]}
                          >
                            Expires{" "}
                            {new Date(item?.termEnd).toLocaleDateString()}
                          </Text>
                        </Flex>
                        <Flex
                          w="100%"
                          justify={"space-between"}
                          align={"center"}
                          pt={5}
                        >
                          <Text
                            size={12}
                            fw={400}
                            color={theme.colors.primaryTextColor[6]}
                          >
                            Charges to
                          </Text>
                          <Text
                            size={12}
                            fw={400}
                            color={theme.colors.primaryTextColor[6]}
                          >
                            Visa **** {item.paymentMethod?.mask}
                          </Text>
                        </Flex>
                      </Flex>
                    </Accordion.Panel>
                  ) : null}
                </Accordion.Item>
              </Accordion>

              <Accordion
                mt={25}
                styles={{
                  control: {
                    padding: "0px",
                    "&:active": {
                      backgroundColor: "rgba(0,0,0,0)",
                    },
                  },
                  chevron: {
                    margin: "0px",
                  },
                  item: {
                    border: "0px",
                  },
                  content: {
                    padding: "0px",
                  },
                }}
                style={{ borderRadius: 4 }}
              >
                <Accordion.Item value={item.id}>
                  <Accordion.Control
                    style={{ color: theme.colors.primaryTextColor[6] }}
                  >
                    <Flex justify={"space-between"} align={"center"}>
                      {
                        <Text
                          size={14}
                          fw={500}
                          color={theme.colors.primaryTextColor[6]}
                        >
                          Signed Documents
                        </Text>
                      }
                    </Flex>
                  </Accordion.Control>

                  <Accordion.Panel>
                    <Flex direction={"column"} pr={25} py={10}>
                      {item?.agreement.additionalPolicies?.map(
                        (policy, index) => {
                          return (
                            <Flex
                              w="100%"
                              py={5}
                              key={index}
                              style={{
                                alignItems: "center",
                                justifyContent: "space-between",
                                color: theme.colors.primaryTextColor[6],
                                background:
                                  index % 2 !== 0 ? theme.colors.light[1] : "",
                                borderTop:
                                  index % 2 !== 0
                                    ? `1px solid ${theme.colors.light[3]}`
                                    : "",
                                borderBottom:
                                  index % 2 !== 0
                                    ? `1px solid ${theme.colors.light[3]}`
                                    : "",
                              }}
                            >
                              <Text size={12} fw={400}>
                                {`${policy?.name}`}
                              </Text>
                              <ActionIcon onClick={() => viewPolicy(item.id)}>
                                <AiFillEye
                                  color={theme.colors.primaryTextColor[6]}
                                />
                              </ActionIcon>
                            </Flex>
                          );
                        }
                      )}
                    </Flex>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Flex>
          );
        })}
    </Flex>
  );
};

export default AgreementListing;
